import { useNavigate } from "react-router-dom";
import { BellIcon } from "@radix-ui/react-icons";
import { Dropdown, DropdownItem } from "../../ui";
import { useDashboardData } from "../../../services/queries";
import PATHS, { ROUTE_PATTERNS, buildRoute } from "../../../constants/Paths";
import { formatAlert } from "../../../utils/alertText";

/**
 * Notification bell, fed by data the app already fetches.
 *
 * The old bell was decorative - no handler, no badge, no popover; a control
 * that promises and does nothing. This one shows the workspace's high-risk
 * alerts from the shared dashboard query (same cache the Dashboard page
 * reads, so the bell costs no extra request when both are mounted) and deep
 * links each alert to its scan report.
 */
export default function NotificationButton() {
  const navigate = useNavigate();
  const { data } = useDashboardData("30d");
  const alerts = data?.alerts || [];

  return (
    <Dropdown
      align="right"
      className="w-80"
      trigger={
        <button
          type="button"
          className="relative inline-flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500"
          aria-label={alerts.length ? `Notifications: ${alerts.length} high-risk alerts` : "Notifications"}
        >
          <BellIcon className="w-5 h-5" />
          {alerts.length > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-semibold text-white">
              {alerts.length > 9 ? "9+" : alerts.length}
            </span>
          )}
        </button>
      }
    >
      <div className="px-3 py-2 border-b border-border">
        <p className="text-sm font-medium text-foreground">High-risk alerts</p>
        <p className="text-xs text-muted-foreground">Last 30 days</p>
      </div>
      {alerts.length === 0 ? (
        <div className="px-3 py-6 text-center text-sm text-muted-foreground">
          Nothing needs your attention.
        </div>
      ) : (
        alerts.slice(0, 6).map((a, i) => {
          // Wire shape is { id, client:{name}, account:{handle}, alert:{...} }.
          // This read `a._id` / `a.clientName` / `a.accountHandle`, which are
          // the aggregation's $project names, not the re-mapped response - so
          // the subtitle was blank and the row never navigated.
          const { category, detail } = formatAlert(a.alert);
          const handle = a.account?.handle;
          return (
            <DropdownItem
              key={a.id || i}
              onClick={() => a.id && navigate(buildRoute(ROUTE_PATTERNS.SCAN_DETAIL, { id: a.id }))}
            >
              <span className="flex items-start gap-2">
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                    a.alert?.severity === "critical" ? "bg-red-600" : "bg-orange-500"
                  }`}
                />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-foreground">
                    {handle ? `@${handle}` : a.client?.name || "Unknown account"}
                    {category ? <span className="font-normal text-muted-foreground"> · {category}</span> : null}
                  </span>
                  {/* line-clamp, not truncate: `truncate` sets white-space:
                      nowrap, which is what let a long finding blow the panel
                      out to the full width of the window. */}
                  <span className="block text-sm text-muted-foreground line-clamp-2">{detail}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {a.client?.name}
                    {a.alert?.timestamp ? `${a.client?.name ? " · " : ""}${new Date(a.alert.timestamp).toLocaleDateString()}` : ""}
                  </span>
                </span>
              </span>
            </DropdownItem>
          );
        })
      )}
      {alerts.length > 6 && (
        <DropdownItem onClick={() => navigate(PATHS.DASHBOARD)}>
          <span className="block text-center text-sm font-medium text-brand-600">
            See all {alerts.length} alerts
          </span>
        </DropdownItem>
      )}
    </Dropdown>
  );
}
